
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import vue2Dropzone, { DropZoneOptions, DropZone } from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import API from '@/lib/API';
import {
  Attachment, AttachmentType, AttachmentTypeOptions, Lead, ProjectsPool, Task,
} from '@/types';

@Component({
  components: {
    vueDropzone: vue2Dropzone,
  },
})
export default class AttachmentsUploadDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  /** Tipo di allegato (singolo). Solo stringa o null/undefined; non supportato come array (usare il dropdown in AttachmentsList). */
  @Prop({ type: String }) readonly type!: AttachmentType | null | undefined;
  @Prop(Object) readonly task!: Task;
  @Prop({ type: Boolean, default: false }) readonly useFather!: boolean

  // Se true, è utilizzato all'interno di una Connection
  @Prop({ type: Boolean, default: false }) readonly connection!: boolean

  // Se true, il componente emette soltanto gli eventi di aggiunta e rimozione senza chiamare le API
  @Prop({ type: Boolean, default: false }) readonly standalone!: boolean

  dialog = false
  valid = false
  loading = false

  newAttachments: Attachment[] = []
  removedAttachments: number[] = []

  dz: DropZone | null = null

  dzOptions: DropZoneOptions = {
    thumbnailWidth: 100,
    thumbnailHeight: 100,
    addRemoveLinks: true,
    removeType: 'server',
    dictCancelUpload: 'Annulla',
    dictCancelUploadConfirmation: 'Confermi di voler annullare il caricamento?',
    dictRemoveFile: 'Rimuovi',
    dictDefaultMessage: 'Trascinare qui i file da caricare<br><small>oppure cliccare</small>',
  }

  @Watch('value')
  onValueChange(val: boolean) {
    console.log(`${this.constructor.name}: onValueChange()`); // eslint-disable-line no-console

    if (val === true) {
      this.updateDropzoneUrl();

      if (this.dz) {
        console.log('AttachmentsUploadDialog: onValueChange() - Dropzone esistente, elimino tutti i file'); // eslint-disable-line no-console

        this.$set(this.dzOptions, 'removeType', 'client');
        this.dz.removeAllFiles();
        this.$set(this.dzOptions, 'removeType', 'server');
      }

      this.newAttachments = [];
      this.removedAttachments = [];
    }
    this.dialog = val;
  }

  updateDropzoneUrl() {
    const uploadType = this.type || 'altri';
    const newUrl = `/upload/${uploadType}`;
    this.$set(this.dzOptions, 'url', newUrl);
    // Aggiorna direttamente l'istanza del dropzone se esiste
    // vue2-dropzone non reagisce automaticamente ai cambiamenti di dzOptions.url
    if (this.dz && 'dropzone' in this.dz && (this.dz as any).dropzone) {
      (this.dz as any).dropzone.options.url = newUrl;
    }
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  get user() {
    return this.$store.getters.currentUser;
  }

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get pool(): ProjectsPool {
    return this.$store.getters.currentPool;
  }

  get father(): Lead | null {
    if (!this.task || 'fatherLeadId' in this.task === false) return null;

    const fatherLeadId = this.task.relationship === 'child'
      ? this.task.fatherLeadId : this.task.LeadId;

    return fatherLeadId && fatherLeadId in this.pool
      ? this.pool[fatherLeadId]
      : null;
  }

  get typeLabel(): string {
    if (!this.type) return 'Altri allegati';
    return this.type in AttachmentTypeOptions ? AttachmentTypeOptions[this.type] : this.type;
  }

  dzMounted() {
    console.log(`${this.constructor.name}: dzMounted()`); // eslint-disable-line no-console

    this.dz = 'attachmentsDropzone' in this.$refs ? this.$refs.attachmentsDropzone : null;
  }

  success(file: { name: string; [key: string]: unknown },
    response: { id: number; [key: string]: unknown }) {
    console.log(`${this.constructor.name}: success() file.name =`, file.name); // eslint-disable-line no-console

    if (response) {
      console.log(`${this.constructor.name}: success() response =`, response); // eslint-disable-line no-console

      this.newAttachments.push({
        id: response.id as number,
        fieldname: response.fieldname as AttachmentType,
        originalname: response.originalname as string,
        encoding: response.encoding as string,
        mimetype: response.mimetype as string,
        destination: response.destination as string,
        filename: response.filename as string,
        path: response.path as string,
        size: response.size as number,
        createdAt: response.createdAt as Date,
      } as Attachment);
    }
  }

  removedFile(file: {
    name: string; xhr: { response: string }; serverId: number; [key: string]: unknown;
  }) {
    console.log(`${this.constructor.name}: removedFile() file.name =`, file.name); // eslint-disable-line no-console

    let serverId: number;
    if (file.xhr) {
      const jsonResponse = JSON.parse(file.xhr.response);
      serverId = jsonResponse.id;
    } else {
      serverId = file.serverId;
    }

    console.log(`AttachmentsUploadDialog: removedFile() - serverId = ${serverId}`); // eslint-disable-line no-console

    this.removedAttachments.push(serverId);

    const aIndex = this.newAttachments.findIndex((a) => a.id === serverId);
    if (aIndex > -1) {
      this.newAttachments.splice(aIndex, 1);
    }
  }

  // Richiede la rimozione dal server degli allegati (aggiunti poi) rimossi in questo dialog
  async removeAttachments() {
    console.log(`${this.constructor.name}: removeAttachments()`); // eslint-disable-line no-console

    if (this.removedAttachments.length > 0) {
      console.log(`${this.constructor.name}: removeAttachments() - rimuovo gli allegati`, this.removedAttachments); // eslint-disable-line no-console

      await API.removeLeadAttachments(
        // Decide se rimuovere da questa lead o dal padre
        this.useFather && this.father
          ? this.father.id
          : this.lead.id, { attachments: this.removedAttachments },
      );

      this.removedAttachments = [];
    } else {
      console.log(`${this.constructor.name}: removeAttachments() - nessun allegato da rimuovere`); // eslint-disable-line no-console
    }
  }

  // Richiede la rimozione dal server di tutti gli allegati
  // aggiunti e rimossi in questo dialog
  async removeAllAttachments() {
    console.log(`${this.constructor.name}: removeAllAttachments()`); // eslint-disable-line no-console

    if (this.newAttachments.length > 0 || this.removedAttachments.length > 0) {
      if (this.useFather && this.father) {
        await API.removeLeadAttachments(this.father.id, {
          attachments: [...this.newAttachments.map((a) => a.id), ...this.removedAttachments],
        });
      } else {
        await API.removeLeadAttachments(this.lead.id, {
          attachments: [...this.newAttachments.map((a) => a.id), ...this.removedAttachments],
        });
      }

      this.newAttachments = [];
      this.removedAttachments = [];
    }
  }

  async onCancel() {
    console.log(`${this.constructor.name}: onCancel()`); // eslint-disable-line no-console

    this.loading = true;

    // Elimina tutti gli allegati prima di chiudere il dialog
    await this.removeAllAttachments();

    this.dialog = false;
    setTimeout(() => {
      this.loading = false;
    }, 500);
  }

  async onAdd() {
    console.log(`${this.constructor.name}: onAdd()`); // eslint-disable-line no-console

    this.loading = true;

    // Richiede la rimozione dal server degli allegati (aggiunti poi) rimossi in questo dialog
    await this.removeAttachments();

    if (this.task || this.connection || this.standalone) {
      // Comunica ad AttachmentsList l'elenco dei nuovi allegati
      // (si occuperà lui eventualmente di aggiungerli al task/lead)
      this.$emit('add', this.newAttachments);
    } else {
      await this.$store.dispatch('updateLead', {
        updateType: 'attachments',
        update: {
          attachments: this.newAttachments.map((a) => a.id),
        },
      });
    }

    this.dialog = false;
    setTimeout(() => {
      this.loading = false;
    }, 500);
  }
}
